type MediaUpdateStatus = "ADDED" | "REMOVED" | "MODIFIED" | "UNCHANGED";

type MediaUpdateAsset = { status: MediaUpdateStatus };

type MediaUpdateHandlers<Asset extends MediaUpdateAsset> = {
  onAdded(asset: Asset & { status: "ADDED" }): Promise<void>;
  onRemoved(asset: Asset & { status: "REMOVED" }): Promise<void>;
  onModified(asset: Asset & { status: "MODIFIED" }): Promise<void>;
  onUnchanged(asset: Asset & { status: "UNCHANGED" }): Promise<void>;
};

function isAdded<Asset extends MediaUpdateAsset>(
  asset: Asset,
): asset is Asset & { status: "ADDED" } {
  return asset.status === "ADDED";
}

function isRemoved<Asset extends MediaUpdateAsset>(
  asset: Asset,
): asset is Asset & { status: "REMOVED" } {
  return asset.status === "REMOVED";
}

function isModified<Asset extends MediaUpdateAsset>(
  asset: Asset,
): asset is Asset & { status: "MODIFIED" } {
  return asset.status === "MODIFIED";
}

function isUnchanged<Asset extends MediaUpdateAsset>(
  asset: Asset,
): asset is Asset & { status: "UNCHANGED" } {
  return asset.status === "UNCHANGED";
}

export async function processMediaUpdates<Asset extends MediaUpdateAsset>(
  assets: ReadonlyArray<Asset>,
  handlers: MediaUpdateHandlers<Asset>,
): Promise<void> {
  for (const asset of assets) {
    if (isAdded(asset)) {
      await handlers.onAdded(asset);
    } else if (isRemoved(asset)) {
      await handlers.onRemoved(asset);
    } else if (isModified(asset)) {
      await handlers.onModified(asset);
    } else if (isUnchanged(asset)) {
      await handlers.onUnchanged(asset);
    }
  }
}
